import type { GovernedMetricSeriesSpec } from './metric-series';
import type { GovernedRankedBreakdownSpec } from './ranked-breakdowns';
import type { AskHelixScenarioRecommendationDto, AskHelixScenarioSetDto } from './plan-payload';
import type { EvidenceRef } from './governed-capability-manifest';
import type { NumericBinding } from './numeric-binding';
import type { GovernedReceiptPointer, GovernedReceiptProof } from './governed-receipt';
import type { GovernedToolOutputRefusalCarry } from './governed-tool-output-refusal';
import type { DecisionAgentSourceClass } from '../../contracts/sourceClasses';
import type { GovernedArgumentExportUnionV1 } from './governed-argument-export';
import type { GovernedRegisteredResultInvocationV1 } from './governed-registered-result-invocation';
import type { ModeledFormulaRuntimeReceipt } from '../../modeled-formula-runtime-receipt';

export const GOVERNED_TOOL_OUTPUT_ENVELOPE_STATUSES = Object.freeze(['answer', 'blocked']);

export type GovernedToolOutputEnvelopeStatus = 'answer' | 'blocked';

export const GOVERNED_TOOL_OUTPUT_ENVELOPE_VERSIONS = Object.freeze([
  'phase1-prep.v1',
  'ask-helix-governed-tool-output.v1',
]);

export const GOVERNED_TOOL_OUTPUT_GAP_SEVERITIES = Object.freeze(['low', 'medium', 'high']);

export const GOVERNED_TOOL_OUTPUT_VIEW_SPEC_FRAGMENT_KINDS = Object.freeze([
  'section',
  'metric',
  'table',
  'callout',
]);

export const GOVERNED_TOOL_OUTPUT_RECONCILIATION_STATUSES = Object.freeze([
  'PASS',
  'FAIL',
  'SKIPPED',
]);

export type GovernedToolOutputSection = {
  id: string;
  title: string;
  body: string;
  /**
   * Capability-owned prose. Governed numeric facts appear only as
   * `{{n:<placeholder>}}` aliases; the manager packet carries `body`
   * unchanged as a governed text fact.
   */
  narrationBodyTemplate?: string;
  /** RT3 I9 — this section IS a disclosure the answer owes, and which one. */
  disclosureKind?: 'population' | 'defaulted_period' | 'defaulted_member';
  evidenceRefIds: string[];
  numericClaimIds: string[];
};

export type GovernedToolOutputNumericClaimReceipt = Readonly<{
  evidenceRefIds: string[];
  calculation: string;
  formulaId?: string;
  formulaVersion?: string;
  invocationId?: string;
  executionEventId?: string;
  modeledFormulaReceipt?: ModeledFormulaRuntimeReceipt;
  reconciliationStatus?: 'PASS' | 'FAIL' | 'SKIPPED';
  reconciliationResidual?: number;
}> &
  (
    | Readonly<{
        sourceValueHash: string;
        receiptPointer?: never;
      }>
    | Readonly<{ sourceValueHash?: never; receiptPointer: GovernedReceiptPointer }>
  );

type SourceHashReceiptWithGovernedReceiptPointer = Readonly<{
  evidenceRefIds: string[];
  calculation: string;
  sourceValueHash: string;
  receiptPointer: GovernedReceiptPointer;
}>;

type ExpectFalse<Value extends false> = Value;
export type GovernedToolOutputNumericClaimReceiptExclusivitySentinel = ExpectFalse<
  SourceHashReceiptWithGovernedReceiptPointer extends GovernedToolOutputNumericClaimReceipt
    ? true
    : false
>;

export type GovernedToolOutputNumericClaim = {
  id: string;
  label: string;
  value: number;
  unit: string;
  hcDelta?: number;
  achievedDeltaHC?: number;
  actionMix?: GovernedToolOutputActionMixItem[];
  scope: string;
  timeframe: string;
  receipt: GovernedToolOutputNumericClaimReceipt;
  overlapAdjustment?: {
    adjustmentType: 'shared_scope_overlap';
    coveredClaimIds: string[];
    overlapAmountUsd: number;
    overlapDimensionKeys: string[];
  };
  overlapExclusion?: AllocationLensExclusionReceipt;
};

export function isPassingApprovedFormulaReceiptLineage(input: {
  invocationId: unknown;
  executionEventId: unknown;
  reconciliationStatus: unknown;
  reconciliationResidual: unknown;
}): boolean {
  return (
    typeof input.invocationId === 'string' &&
    input.invocationId.trim().length > 0 &&
    typeof input.executionEventId === 'string' &&
    input.executionEventId.trim().length > 0 &&
    input.reconciliationStatus === 'PASS' &&
    typeof input.reconciliationResidual === 'number' &&
    Number.isFinite(input.reconciliationResidual) &&
    Math.abs(input.reconciliationResidual) <= 0.01
  );
}

export type AllocationLensExclusionMemberUniverse = Readonly<{
  completeness: 'complete' | 'incomplete';
  memberCount: number;
  memberDigest: string;
  provenance: AllocationLensExclusionMemberUniverseProvenance;
}>;

export type AllocationLensExclusionMemberUniverseProvenance = Readonly<{
  sourceClass: DecisionAgentSourceClass;
  evidenceRefIds: readonly string[];
  sourceRef: string;
  artifactId: string;
  ingestJobId: string;
  sourceSystemRef: string;
  evidenceSchemaVersion: string;
  contextKind: string;
  sourceAsOf: string;
  contentDigest: string;
}>;

export type AllocationLensExclusionReceipt = Readonly<{
  schemaVersion: 'allocation_lens_exclusion_receipt_v1';
  formulaId: 'allocation_lens_exclusion_receipt_v1';
  formulaVersion: '1.0.0';
  formulaDefinitionDigest: string;
  formulaExecutionEventId: string;
  formulaOutputDigest: string;
  receiptId: string;
  artifactId: string;
  sourceSystemRef: 'ask_helix_memory:allocation_lens_exclusion_receipt_v1';
  sourceRef: string;
  source: Readonly<{
    sourceClass: 'connector-derived';
    sourceRef: string;
    ingestJobId: string;
    sourceSystemRef: 'ask_helix_memory:allocation_lens_exclusion_receipt_v1';
  }>;
  tenantId: string;
  scopeKind: 'enterprise';
  scopeId: string;
  pairIdentity: Readonly<{
    leftLeverFamilyId: string;
    rightLeverFamilyId: string;
  }>;
  coveredClaimIds: readonly [string, string];
  reportingCutIdentity: string;
  reportingCutAsOf: string;
  timeframe: string;
  sourceAsOf: string;
  comparisonDimension: 'atomic_p_and_l_action';
  comparisonRuleId: 'allocation_lens_atomic_action_non_intersection';
  comparisonRuleVersion: '1.0.0';
  comparedMemberUniverses: Readonly<{
    left: AllocationLensExclusionMemberUniverse;
    right: AllocationLensExclusionMemberUniverse;
  }>;
  overlapMemberCount: 0;
  overlapMemberDigest: string;
  verdict: 'zero_overlap';
  overlapAmountUsd: 0;
  coveredClaimSourceValueHashes: readonly Readonly<{
    claimId: string;
    sourceValueHash: string;
  }>[];
  evidenceRefIds: readonly string[];
  lifecycle: Readonly<{
    status: 'APPROVED';
    createdByUserId: string;
    reviewedByUserId: string;
    reviewedAt: string;
    approvedByUserId: string;
    approvedAt: string;
  }>;
}>;

export type GovernedToolOutputActionMixItem = {
  leverId: string;
  label: string;
  savingsRunRateUsd: number;
  headcountDelta: number;
  actionCount: number;
};

export type PoolFootprintAtomKind = 'cost_pool' | 'position' | 'account' | 'lane' | 'revenue_line';

export type PoolFootprintAtom = Readonly<{
  atomKind: PoolFootprintAtomKind;
  atomId: string;
  scope: string;
  timeWindow: string;
  drawnUsd: number;
}>;

export type AttributedPoolFootprint = Readonly<{
  footprintStatus: 'attributed';
  atoms: readonly PoolFootprintAtom[];
}>;

export type UnattributedPoolFootprint = Readonly<{
  footprintStatus: 'unattributed';
  atoms?: readonly [];
}>;

export type PoolFootprint = AttributedPoolFootprint | UnattributedPoolFootprint;

export type ImpactClaim = Readonly<{
  claimId: string;
  sourceCapabilityId: string;
  metricKey: 'EBITDA' | 'Margin' | 'Revenue' | 'Cost' | (string & {});
  valueBindingId: string;
  costPoolIds?: readonly string[];
  accountIds?: readonly string[];
  segmentIds?: readonly string[];
  customerIds?: readonly string[];
  repIds?: readonly string[];
  overlapDimensionKeys?: readonly string[];
  glAccountIds?: readonly string[];
  orgNodeIds?: readonly string[];
  laneIds?: readonly string[];
  carrierIds?: readonly string[];
  initiativeIds?: readonly string[];
  reportingCutIdentity?: string;
  timeWindow: string;
  claimType: 'run_rate' | 'one_time' | 'cash' | 'p_and_l' | 'capacity' | 'risk';
  poolFootprint?: PoolFootprint;
}>;

export type GovernedToolOutputAssumption = {
  id: string;
  label: string;
};

/**
 * #5361 r16 (ChatGPT HOLD @7b64a30545) — WHICH ANSWER ANCHOR A GAP IS ABOUT,
 * stated by the PRODUCER that authored it.
 *
 * Publication needs this to decide whether a runtime-granted operation still
 * owes the reader a gap about an anchor nobody asked for. It used to infer the
 * answer by reinterpreting the gap `id` after the fact — splitting on a colon
 * and matching the tail against the manifest's `optionalMetricKeys` — which is
 * a semantic authority decision made from an id naming convention, downstream
 * of the producer fence, and it silently suppressed any unrelated gap whose id
 * happened to end in the same suffix. The relation is declared here instead.
 *
 * `relevance` mirrors the manifest's own required/optional anchor split
 * (`answerAnchors.metricKeys` vs `answerAnchors.optionalMetricKeys`): the
 * producer classifies against the SAME declaration, so no second notion of
 * "optional" exists. A gap that is not about an answer anchor carries no
 * `anchor` at all and is never dropped by anyone.
 */
export type GovernedToolOutputGapAnchor = {
  metricKey: string;
  relevance: 'required' | 'optional';
};

/**
 * #5361 r18 (ChatGPT arch-review) — WHAT A RUNTIME-GRANTED EXECUTION WAS
 * GRANTED FOR.
 *
 * Obligation-driven candidacy executes contributions and bridge authorities the
 * reader never selected. Such an execution owes the reader the gaps of ITS OWN
 * grant purpose and nothing else — a gap about an optional anchor nobody asked
 * for is noise the reader must not be handed.
 *
 * r16/r17 enforced that by DELETING gaps after the fact, in narration. That put
 * a governed-envelope decision downstream of the producer fence, where the
 * wrong authority was making it. The scope now travels INTO execution instead:
 * the producer authors an envelope already scoped to its grant, and every
 * consumer downstream reads a governed envelope it never edits.
 *
 * Absent grant = a planner-selected execution, which emits every lawful gap.
 */
/**
 * #5361 r18 (ChatGPT structural audit) — WHO chose this execution, stated by
 * the seam that chose it. Nothing downstream infers it from the ABSENCE of
 * something else (a call id missing from a planned set, a grant field left
 * undefined): absence is not a fact, and inferring authority from it is how a
 * renamed field or a reordered list silently changes what a producer
 * publishes.
 *
 * A discriminated union, so an UNSCOPED authority carries no scope to
 * misread: the reader asked for a planner-selected intent whole, and only a
 * runtime grant is limited to the anchors it was granted for.
 */
export type GovernedAnswerAnchorGrant =
  | { authority: 'planner_selected' }
  | {
      authority: 'runtime_granted';
      scope: 'anchor_subset';
      /** The answer anchors this execution was granted for. */
      metricKeys: readonly string[];
    };

export type GovernedToolOutputGap = {
  id: string;
  severity: 'low' | 'medium' | 'high';
  description: string;
  /**
   * The evidence keys the gate actually found missing (#3943). Structured so a
   * blocked answer can name the real missing data instead of borrowing example
   * nouns from its own prompt. Absent when the gate recorded no itemised key.
   */
  missingEvidenceKeys?: string[];
  /**
   * #5361 r16 — the answer anchor this gap is about, when it is about one.
   * Absent means "not an answer-anchor gap", which is always reader-relevant.
   */
  anchor?: GovernedToolOutputGapAnchor;
  /**
   * #5453 — RECEIPT/LOG ONLY, NEVER READER-FACING. The internal diagnostic a
   * gate authored for this block (e.g. the evidence-posture domain/status/
   * required-evidence trace) — kept for the server log and the gate ledger,
   * never for `answerMarkdown` or `gaps`. The narration pipeline projects only
   * `description` (see `readerSafeGaps`); nothing reads this field to build a
   * reader-facing surface, and nothing may start doing so.
   */
  internalDiagnostic?: string;
};

export type StrategyAttachableFinding = {
  schemaVersion: 'strategy_attachable_finding_v1';
  findingId: string;
  strategyId: string | null;
  objectiveRefId?: string | null;
  capabilityRefId?: string | null;
  sourceModuleId: string;
  sourceLeverFamilyId?: string | null;
  findingType:
    'alignment_gap' | 'capability_gap' | 'capacity_gap' | 'constraint_risk' | 'evidence_gap';
  severity: 'low' | 'medium' | 'high';
  summary: string;
  evidenceRefIds: string[];
};

export type GovernedToolOutputViewSpecFragment = {
  id: string;
  kind: 'section' | 'metric' | 'table' | 'callout';
  title: string;
  sectionIds: string[];
  numericClaimIds: string[];
};

export type GovernedToolOutputEvidenceRef = {
  id: string;
  sourceType: string;
  sourceId: string;
  asOf: string;
  description: string;
  /**
   * #5361 M2 commit 2 (H0-owned) — the per-clause proof field
   * `manager-loop-observation-coverage.ts`'s `provenByGovernedEvidence` was
   * built to read (see that file's module doc comment, M2 commit 1 r2).
   *
   * SERVER-OWNED, PRODUCER-STAMPED: only the capability that authored this
   * evidence ref may set it, at emission time — never inferred downstream
   * from an id naming convention, never edited by the manager, planner, or
   * narrator. Values are exact clause ids drawn from the FROZEN objective's
   * own `constraintIds`/`exclusionIds` (the same id sets
   * `manager-run-reducer.ts`'s `assertCoverageIds` already requires every
   * claimed constraint/exclusion id to belong to) — never a fresh id space,
   * never a numeric obligation id. A ref carrying no `boundClauseIds` (or an
   * empty array) proves no qualitative clause; it may still cover a numeric
   * claim exactly as before, unaffected by this field.
   */
  boundClauseIds?: readonly string[];
  receiptPointer?: GovernedReceiptPointer;
} & Partial<EvidenceRef>;

export type GovernedFact = {
  factId: string;
  label: string;
  value: unknown;
  evidenceRefIds: string[];
  /**
   * #7254 item 3 — additive, optional. When a producer names how this fact
   * was sourced, the offline narration floor (narrate.ts's
   * governedResultFloor) discloses it as a parenthetical label (e.g.
   * "(modeled)") beside the fact's verbatim value; an absent sourceClass
   * renders with no badge, exactly as before this field existed.
   */
  sourceClass?: DecisionAgentSourceClass;
};

/** A producer-selected column in a reader-facing detail table. */
export type GovernedToolOutputRowsColumn = Readonly<{
  key: string;
  label: string;
}>;

/** One display-only row. Business-number formatting remains producer-owned. */
export type GovernedToolOutputRowsEntry = Readonly<{
  key: string;
  /** The evidence-bound fact this row projects from this same envelope. */
  factRefId: string;
  cells: Readonly<Record<string, string | null>>;
  /** Numeric cells resolve through an existing envelope binding, never a second value channel. */
  numericBindingRefs?: Readonly<Record<string, string>>;
}>;

/**
 * Generic structured detail the manager may publish unchanged to a reader.
 * Unlike `facts`, this carries an explicit, public-safe column/cell contract;
 * unlike `viewSpecFragments`, it carries the rows themselves. It is the single
 * producer transport for typed list/table answer blocks.
 */
export type GovernedToolOutputRowsBlock = Readonly<{
  id: string;
  title: string;
  presentation?: 'expanded' | 'disclosure';
  columns: readonly GovernedToolOutputRowsColumn[];
  rows: readonly GovernedToolOutputRowsEntry[];
}>;

export type GovernedCommitmentFact = Readonly<{
  ref: string;
  displayLabel: string;
  bindingStatus: 'present' | 'absent';
  metricRef: string | null;
  evidenceStatus: 'declared' | 'not_declared';
  proofStatus: 'validated' | 'not_validated';
  evidenceRefs: readonly string[];
  /** Recorded promise-set approval time, when the producer has one. */
  approvedAt?: string;
  /** Recorded commitment target date, never inferred from a missing reference. */
  targetDate?: string;
}>;

export type GovernedToolOutputLineage = {
  contextRevisionId: string;
  capabilityCatalogRevisionId: string;
  manifestRevisionId: string;
  sourceRunIds?: string[];
};

/**
 * Producer-owned aliases for a comparison or explanatory relationship. These
 * records never carry values: each key must resolve through this envelope's
 * `numericBindings` before any reader surface may use the relationship.
 */
export const GOVERNED_NARRATION_COMPARISON_BASES = [
  'target',
  'plan',
  'prior_period',
  'benchmark',
  'peer',
] as const;
export type GovernedNarrationComparisonBasis = (typeof GOVERNED_NARRATION_COMPARISON_BASES)[number];

export const GOVERNED_NARRATION_RELATION_KINDS = [
  'component',
  'deduction',
  'exclusion',
  'constraint',
  'attribution',
] as const;
export type GovernedNarrationRelationKind = (typeof GOVERNED_NARRATION_RELATION_KINDS)[number];

export type GovernedToolOutputComparisonReference = Readonly<{
  basis: GovernedNarrationComparisonBasis;
  subjectBindingKey: string;
  referenceBindingKey: string;
  deltaBindingKey?: string;
  gapBindingKey?: string;
  coverageRatioBindingKey?: string;
  scopePeriodCompatibility?: 'same_scope_same_period' | 'declared_compatible';
}>;

export type GovernedToolOutputRelationshipRow = Readonly<{
  kind: GovernedNarrationRelationKind;
  subjectBindingKey: string;
  partBindingKey: string;
  label?: string;
}>;

export type GovernedToolOutputRelationshipReferences = Readonly<{
  comparisons?: readonly GovernedToolOutputComparisonReference[];
  relations?: readonly GovernedToolOutputRelationshipRow[];
}>;

export type GovernedToolOutputEnvelope = {
  envelopeVersion: 'phase1-prep.v1' | 'ask-helix-governed-tool-output.v1';
  moduleId?: string;
  capabilityId?: string;
  toolName?: string;
  runId: string;
  status: GovernedToolOutputEnvelopeStatus;
  governedSections: GovernedToolOutputSection[];
  numericClaims: GovernedToolOutputNumericClaim[];
  evidenceRefs: GovernedToolOutputEvidenceRef[];
  assumptions: GovernedToolOutputAssumption[];
  gaps: GovernedToolOutputGap[];
  blockedReasons: string[];
  /**
   * The typed refusal behind a blocked envelope, carried whole — the producer's
   * own particulars and the narrower request it would have accepted. Present
   * only on a refusal that HAS one; absence is honest, not a gap (#7929).
   */
  refusalCarry?: GovernedToolOutputRefusalCarry;
  viewSpecFragments: GovernedToolOutputViewSpecFragment[];
  facts?: GovernedFact[];
  rows?: readonly GovernedToolOutputRowsBlock[];
  commitmentFacts?: readonly GovernedCommitmentFact[];
  sections?: GovernedToolOutputSection[];
  numericBindings?: NumericBinding[];
  /** Full producer receipts are carried once; public figures point to them by receipt identity and field path. */
  governedReceiptProofs?: readonly GovernedReceiptProof[];
  /**
   * Typed, producer-declared alias relationships. A numeric reconciliation's
   * raw component amount is never relationship authority; producers that need
   * narration enrichment declare the relation here, otherwise it is omitted.
   */
  relationshipReferences?: GovernedToolOutputRelationshipReferences;
  impactClaims?: ImpactClaim[];
  lineage?: GovernedToolOutputLineage;
  strategyAttachableFinding?: StrategyAttachableFinding | null;
  /**
   * #5042 Story 2 — an OPAQUE producer block. A capability that ran its own
   * governed scenario cases publishes the finished `scenario_set` payload here;
   * the manager carries it to the public response unread. Nothing downstream
   * constructs, edits, re-derives or validates its business content: every
   * figure it names is a placeholder that must already exist in this envelope's
   * `numericBindings[]` and clear the engine-bound fence, which is the producer's
   * invariant to hold.
   */
  scenarioSet?: AskHelixScenarioSetDto;
  /**
   * #5140 Q6 P0 — the PRODUCER'S OWN close, carried unchanged.
   *
   * A capability whose contract declares `recommendationPolicy: 'required'`
   * with `recommendationAuthoritySource: 'producer_scenario'` already decided
   * what it advises and why. Rebuilding the public envelope without this field
   * discarded that decision and left narration to invent the close or omit it.
   * Nothing downstream writes here: the manager, the planner and the narrator
   * all read it or leave it alone.
   *
   * It rides the ENVELOPE rather than only the `scenarioSet` because a partial
   * result — numbers withheld by the engine-bound fence, lawful prose intact —
   * still owes the reader the producer's advice.
   */
  recommendation?: AskHelixScenarioRecommendationDto;
  /**
   * THE TREND, AS PLACEHOLDERS. A read that publishes a governed figure per
   * period declares the line those figures form here: labels, order, unit,
   * scope and span, plus one `placeholder` per point into this envelope's own
   * `numericBindings[]`.
   *
   * The spec carries NO figure, so it opens no second numeric channel: the
   * manager mints every `displayValue` from the named binding through its one
   * display authority, and a point naming no surviving governed fact publishes
   * a disclosed absence rather than a zero. A period the producer could not
   * read is simply not a point — it is a NAMED GAP on this envelope.
   */
  metricSeries?: GovernedMetricSeriesSpec[];
  /**
   * THE LEADERBOARD, AS PLACEHOLDERS (#6164). A read that publishes a governed
   * figure per group declares the ranking those figures form here: labels and
   * producer ORDER (order is the ranking authority), plus one `placeholder`
   * per row into this envelope's own `numericBindings[]` — and, when a share
   * exists, a `sharePlaceholder` into a ratio binding the producer PUBLISHED.
   *
   * Same doctrine as `metricSeries` above: the spec carries NO figure, the
   * manager mints every `displayValue` through its one display authority, and
   * a row naming no surviving governed fact publishes a disclosed absence.
   */
  rankedBreakdowns?: GovernedRankedBreakdownSpec[];
  /**
   * #5841 — producer-declared exports of this envelope's OWN already-published
   * bindings, safe to hand to a later operation in the same run as a
   * server-minted trusted reference. An export never carries a second numeric
   * value; it points at `numericBindings[].placeholder`. Only the
   * manager-owned `projectGovernedArgumentExports` (in the manager-runtime
   * loop) may turn one of these into a `ManagerTrustedReference` — nothing
   * downstream trusts this field on its own.
   */
  argumentExports?: readonly GovernedArgumentExportUnionV1[];
  registeredResultInvocations?: readonly GovernedRegisteredResultInvocationV1[];
};
